import React, { useState } from 'react';
import { useRequest } from 'ahooks';
import { Spin } from 'antd';
import compressorImg from '../../imgs/load.png';
import compressorStopImg from '../../imgs/stop.png';
import compressorUnloadImg from '../../imgs/unload.png';
import compressorDefaultImg from '../../imgs/default.png';
import styles from './index.module.scss';
import { Button, Tooltip } from '@maxtropy/components';
import { DoubleRightOutlined } from '@ant-design/icons';
import tittleDecoration from '@/assets/images/title_icon.png';
import StationDetailModal from '../StationDetailModal';
import {
  apiV2AirCompressorOverviewTotalPost,
  V2AirCompressorOverviewTotalPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { formatNumber, formatValueWithWan, OVERVIEW_POLLING_INTERVAL } from '../../utils';

type StationData = NonNullable<V2AirCompressorOverviewTotalPostResponse['compressDataVoList']>[number];

const getCompressorStatusImg = (status?: number) => {
  if (status === null || typeof status === 'undefined') return compressorDefaultImg;
  if (status === 0) return compressorStopImg;
  if (status === 2) return compressorUnloadImg;
  return compressorImg;
};

const getCompressorStatus = (status?: number) => {
  if (status === 0) {
    return { text: '停机', color: '#FF4D4F' };
  }
  if (status === 1) {
    return { text: '加载', color: '#59D744' };
  }
  if (status === 2) {
    return { text: '卸载', color: '#FFCB47' };
  }
  return { text: '未知', color: '#5D7092' };
};

const deviceTypeLabelMap: Record<number, string> = {
  1: '新',
  2: '旧',
};

const getDeviceShortName = (name?: string) => {
  if (!name) return '--';
  return name.length > 5 ? name.slice(5) : name;
};

const getTooltipContainer = () => document.getElementById('full_screen_box') ?? document.body;

const getDeviceTypeBadge = (device: { type?: number; spec?: number }): React.ReactNode => {
  if (device.type === undefined && device.spec === undefined) {
    return null;
  }
  const typeText = device.type !== undefined ? deviceTypeLabelMap[device.type] || '--' : '--';
  const specText = device.spec ?? '--';
  const isNew = device.type === 1;
  return (
    <span className={`${styles.deviceTypeBadge} ${isNew ? styles.deviceTypeBadgeNew : styles.deviceTypeBadgeOld}`}>
      {typeText}({specText})
    </span>
  );
};

const StationBlockItem: React.FC<{
  station?: StationData;
  index: number;
  gasUnit: string;
  electricUnit: string;
  gasElectricUnit: string;
  pressureUnit: string;
  flowUnit: string;
}> = ({ station, index, gasUnit, electricUnit, gasElectricUnit, pressureUnit, flowUnit }) => {
  const stationIndex = index + 1;
  const deviceCards = station?.compressDataList ?? [];
  const metrics = station?.deviceDataList ?? [];
  const [detailState, setDetailState] = useState<{ open: boolean; compressorId?: number | string }>({ open: false });
  const stationScadaId = station?.scadaId;
  const gasProduction = formatValueWithWan(station?.gasProduction, gasUnit);
  const powerConsumption = formatValueWithWan(station?.powerConsumption, electricUnit);

  const handleViewScada = () => {
    if (!stationScadaId) {
      return;
    }
    window.open(`/station/scada/building?stationId=${stationScadaId}`, '_blank');
  };

  const handleOpenDetail = () => {
    setDetailState({ open: true });
  };

  const handleOpenCompressorDetail = (id?: number | string) => {
    setDetailState({ open: true, compressorId: id });
  };

  const handleCloseDetail = () => {
    setDetailState({ open: false });
  };

  return (
    <section className={styles.stationBlock}>
      <div className={styles.stationHeader}>
        <div className={styles.formTitle}>
          <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
          <span className={styles.stationTitle}>{`${stationIndex}#空压站`}</span>
        </div>
        <div className={styles.stationActions}>
          <Button type="link" style={{ padding: 0 }} onClick={handleViewScada} disabled={!stationScadaId}>
            <div className={styles.linkInner}>
              <span>查看组态</span>
              <DoubleRightOutlined />
            </div>
          </Button>
          <Button type="link" style={{ padding: 0 }} onClick={handleOpenDetail}>
            <div className={styles.linkInner}>
              <span>详情</span>
              <DoubleRightOutlined />
            </div>
          </Button>
        </div>
        <div className={styles.stationPressure}>
          <span>{`实时总管压力(${pressureUnit}):`}</span>
          <strong>{formatNumber(station?.pressure)}</strong>
        </div>
      </div>

      <div className={styles.stationBody}>
        <div className={styles.deviceGrid}>
          {deviceCards.map(device => {
            const status = getCompressorStatus(device.status);
            return (
              <div key={`${stationIndex}-${device.id || device.name}`} className={styles.deviceItem}>
                <div className={styles.deviceImgWrap}>
                  {getDeviceTypeBadge(device)}
                  <Tooltip
                    placement="top"
                    getPopupContainer={getTooltipContainer}
                    overlayClassName={styles.compressorTooltip}
                    title={
                      <div className={styles.tooltipContent}>
                        <div className={styles.tooltipName}>{device.name || '--'}</div>
                        <div className={styles.tooltipStatus}>
                          <span className={styles.tooltipStatusDot} style={{ background: status.color }} />
                          <span className={styles.tooltipStatusText} style={{ color: status.color }}>
                            {status.text}
                          </span>
                        </div>
                      </div>
                    }
                  >
                    <img
                      className={styles.compressorImg}
                      src={getCompressorStatusImg(device.status)}
                      alt={device.name || '--'}
                      onClick={() => handleOpenCompressorDetail(device.id)}
                    />
                  </Tooltip>
                </div>
                <div className={styles.deviceName}>{getDeviceShortName(device.name)}</div>
              </div>
            );
          })}
        </div>

        <div className={styles.stationAside}>
          <div className={styles.stationAsideTop}>
            <div className={styles.stationStatCard}>
              <div className={styles.stationStatLabel}>今日气电比</div>
              <div className={styles.stationStatValue}>{formatNumber(station?.gasPowerRatio, 4)}</div>
              <div className={styles.stationStatUnit}>{gasElectricUnit}</div>
            </div>

            <div className={styles.stationSummary}>
              <div className={styles.stationSummaryItem}>
                <div className={styles.stationSummaryLabel}>{`${stationIndex}#空压站今日产气量(${gasUnit})`}</div>
                <div className={styles.stationSummaryPrimary}>{gasProduction.displayText}</div>
              </div>
              <div className={styles.stationSummaryItem}>
                <div className={styles.stationSummaryLabel}>{`今日总计耗电量(${electricUnit})`}</div>
                <div className={styles.stationSummarySecondary}>{powerConsumption.displayText}</div>
              </div>
            </div>
          </div>

          <div className={styles.stationFlowSummary}>
            <span className={styles.stationFlowSummaryLabel}>{`${stationIndex}#空总计瞬时流量`}</span>
            <strong className={styles.stationFlowSummaryValue}>{`${formatNumber(station?.flow)} ${flowUnit}`}</strong>
          </div>

          <div className={styles.stationMetrics}>
            {metrics.map(item => (
              <div key={item.deviceName} className={styles.metricRow}>
                <span className={styles.metricLabel}>{item.deviceName || '--'}</span>
                <strong className={styles.metricPressure}>
                  {typeof item.pressure === 'number' ? `${formatNumber(item.pressure)} ${pressureUnit}` : '--'}
                </strong>
                <strong className={styles.metricFlow}>
                  {typeof item.flow === 'number' ? `${formatNumber(item.flow)} ${flowUnit}` : '--'}
                </strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <StationDetailModal
        open={detailState.open}
        onClose={handleCloseDetail}
        stationTitle={
          detailState.compressorId
            ? `${station?.compressDataList?.find(item => item.id === detailState.compressorId)?.name || '空压机'}`
            : `${stationIndex}#空压站`
        }
        scopeType={detailState.compressorId ? 'compressor' : 'station'}
        stationIds={station?.id ? [station.id] : undefined}
        compressorId={detailState.compressorId}
      />
    </section>
  );
};

const StationBlock: React.FC = () => {
  const { data, loading } = useRequest(() => apiV2AirCompressorOverviewTotalPost({}), {
    pollingInterval: OVERVIEW_POLLING_INTERVAL,
  });
  const stations = data?.compressDataVoList ?? [];
  const gasUnit = data?.gasUnit || '-';
  const electricUnit = data?.electricUnit || '-';
  const gasElectricUnit = data?.gasElectricUnit || '-';
  const pressureUnit = data?.pressureUnit || '-';
  const flowUnit = data?.flowUnit || '-';

  return (
    <div className={styles.stationGrid}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Spin key={stations[index]?.id || index} spinning={loading && !data} wrapperClassName={styles.stationLoading}>
          <StationBlockItem
            station={stations[index]}
            index={index}
            gasUnit={gasUnit}
            electricUnit={electricUnit}
            gasElectricUnit={gasElectricUnit}
            pressureUnit={pressureUnit}
            flowUnit={flowUnit}
          />
        </Spin>
      ))}
    </div>
  );
};

export default StationBlock;
